/**
 * ImpactSummaryPanel - Shows at-a-glance metrics for AutoPark preview
 *
 * Displays:
 * - Locked count (staying put)
 * - Moving count (will change spots)
 * - New placements (previously unassigned)
 * - Conflicts (couldn't be placed)
 * - Utilization before/after comparison
 */

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Collapse, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ImpactSummary } from "../types";

interface ImpactSummaryPanelProps {
  impactSummary: ImpactSummary;
  // Hover callbacks for Gantt highlighting
  onMoveHover?: (tripId: string | null) => void;
  onConflictHover?: (conflict: { tripId: string; startTime: string; endTime: string } | null) => void;
  // Click callback to pin/select a conflict
  onConflictClick?: (conflict: { tripId: string; startTime: string; endTime: string } | null) => void;
  // Currently selected/pinned conflict
  selectedConflictId?: string | null;
}

export const ImpactSummaryPanel: React.FC<ImpactSummaryPanelProps> = ({
  impactSummary,
  onMoveHover,
  onConflictHover,
  onConflictClick,
  selectedConflictId,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const {
    lockedCount,
    movingCount,
    newPlacementCount,
    conflictCount,
    utilizationBefore,
    utilizationAfter,
    moves,
    conflicts,
  } = impactSummary;

  const utilizationChange = utilizationAfter - utilizationBefore;
  const hasDetails = moves.length > 0 || conflicts.length > 0;

  const unknownLabel = t("screens.ramp.autoPark.impactSummary.unknown");

  // Group moves by destination spot for better readability
  const movesByDestination = useMemo(() => {
    const grouped = new Map<string, typeof moves>();
    moves.forEach((move) => {
      const dest = move.toSpot || unknownLabel;
      if (!grouped.has(dest)) grouped.set(dest, []);
      grouped.get(dest)!.push(move);
    });
    return Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [moves, unknownLabel]);

  // Group conflicts by reason
  const conflictsByReason = useMemo(() => {
    const grouped = new Map<string, typeof conflicts>();
    conflicts.forEach((c) => {
      const reason = c.reason || unknownLabel;
      if (!grouped.has(reason)) grouped.set(reason, []);
      grouped.get(reason)!.push(c);
    });
    return Array.from(grouped.entries());
  }, [conflicts, unknownLabel]);

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: "divider",
        bgcolor: "grey.50",
        borderLeft: conflictCount > 0 ? 4 : 0,
        borderLeftColor: conflictCount > 0 ? "warning.main" : "transparent",
        px: 2,
        py: 1,
      }}
    >
      {/* Summary Row */}
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={3} alignItems="center">
          {/* Locked */}
          <Tooltip title={t("screens.ramp.autoPark.impactSummary.tooltips.locked", { count: lockedCount })}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LockIcon fontSize="small" color="success" />
              <Typography variant="body2">
                <strong>{lockedCount}</strong> {t("screens.ramp.autoPark.impactSummary.locked")}
              </Typography>
            </Stack>
          </Tooltip>

          {/* Moving */}
          <Tooltip title={t("screens.ramp.autoPark.impactSummary.tooltips.moving", { count: movingCount })}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <SwapHorizIcon fontSize="small" color="info" />
              <Typography variant="body2">
                <strong>{movingCount}</strong> {t("screens.ramp.autoPark.impactSummary.moving")}
              </Typography>
            </Stack>
          </Tooltip>

          {/* New Placements */}
          {newPlacementCount > 0 && (
            <Tooltip title={t("screens.ramp.autoPark.impactSummary.tooltips.new", { count: newPlacementCount })}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <AddCircleOutlineIcon fontSize="small" color="info" />
                <Typography variant="body2">
                  <strong>{newPlacementCount}</strong> {t("screens.ramp.autoPark.impactSummary.new")}
                </Typography>
              </Stack>
            </Tooltip>
          )}

          {/* Conflicts */}
          {conflictCount > 0 && (
            <Tooltip title={t("screens.ramp.autoPark.impactSummary.tooltips.conflicts", { count: conflictCount })}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <WarningIcon fontSize="small" color="warning" />
                <Typography variant="body2" color="warning.dark">
                  <strong>{conflictCount}</strong> {t("screens.ramp.autoPark.impactSummary.conflicts")}
                </Typography>
              </Stack>
            </Tooltip>
          )}

          {/* Utilization */}
          <Tooltip title={t("screens.ramp.autoPark.impactSummary.tooltips.utilization", { before: utilizationBefore, after: utilizationAfter })}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TrendingUpIcon
                fontSize="small"
                color={utilizationChange >= 0 ? "success" : "error"}
              />
              <Typography variant="body2">
                {utilizationBefore}% → {utilizationAfter}%
                {utilizationChange !== 0 && (
                  <Typography
                    component="span"
                    variant="body2"
                    color={utilizationChange > 0 ? "success.main" : "error.main"}
                    sx={{ ml: 0.5 }}
                  >
                    ({utilizationChange > 0 ? "+" : ""}
                    {utilizationChange}%)
                  </Typography>
                )}
              </Typography>
            </Stack>
          </Tooltip>
        </Stack>

        {/* Expand button for details */}
        {hasDetails && (
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Stack>

      {/* Expanded Details */}
      <Collapse in={expanded}>
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider", maxHeight: 200, overflowY: "auto" }}>
          {/* Move Details - grouped by destination spot */}
          {moves.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                {t("screens.ramp.autoPark.impactSummary.details.moves", { count: moves.length })}
              </Typography>
              {movesByDestination.map(([spotName, spotMoves]) => (
                <Box key={spotName} sx={{ display: "flex", alignItems: "baseline", mb: 0.5, ml: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ minWidth: 70 }}>
                    {spotName} ({spotMoves.length}):
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, ml: 1 }}>
                    {spotMoves.map((move, idx) => (
                      <Typography
                        key={move.trip_id}
                        component="span"
                        variant="caption"
                        onMouseEnter={() => onMoveHover?.(move.trip_id)}
                        onMouseLeave={() => onMoveHover?.(null)}
                        sx={{
                          cursor: "pointer",
                          fontFamily: "monospace",
                          "&:hover": { color: "primary.main", textDecoration: "underline" },
                        }}
                      >
                        {move.tail_number || move.trip_id.slice(0, 6)}{idx < spotMoves.length - 1 ? "," : ""}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Conflict Details - grouped by reason */}
          {conflicts.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={600} color="warning.dark" sx={{ mb: 0.5, display: "block" }}>
                {t("screens.ramp.autoPark.impactSummary.details.conflicts", { count: conflicts.length })}
              </Typography>
              {conflictsByReason.map(([reason, reasonConflicts]) => (
                <Box key={reason} sx={{ display: "flex", alignItems: "baseline", mb: 0.5, ml: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="warning.dark" sx={{ minWidth: 180 }}>
                    {reason}:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, ml: 1 }}>
                    {reasonConflicts.map((conflict, idx) => {
                      const isSelected = selectedConflictId === conflict.trip_id;
                      return (
                        <Typography
                          key={conflict.trip_id}
                          component="span"
                          variant="caption"
                          onMouseEnter={() => {
                            if (conflict.start_time && conflict.end_time && !selectedConflictId) {
                              onConflictHover?.({
                                tripId: conflict.trip_id,
                                startTime: conflict.start_time,
                                endTime: conflict.end_time,
                              });
                            }
                          }}
                          onMouseLeave={() => {
                            if (!selectedConflictId) {
                              onConflictHover?.(null);
                            }
                          }}
                          onClick={() => {
                            if (conflict.start_time && conflict.end_time) {
                              if (isSelected) {
                                // Deselect
                                onConflictClick?.(null);
                              } else {
                                // Select
                                onConflictClick?.({
                                  tripId: conflict.trip_id,
                                  startTime: conflict.start_time,
                                  endTime: conflict.end_time,
                                });
                              }
                            }
                          }}
                          sx={{
                            cursor: "pointer",
                            fontFamily: "monospace",
                            fontWeight: isSelected ? 700 : 400,
                            bgcolor: isSelected ? "warning.light" : "transparent",
                            px: isSelected ? 0.5 : 0,
                            borderRadius: 0.5,
                            "&:hover": { color: "warning.dark", textDecoration: "underline" },
                          }}
                        >
                          {conflict.tail_number || conflict.trip_id.slice(0, 6)}{idx < reasonConflicts.length - 1 ? "," : ""}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
